<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    includeFees: boolean;
    feesAmount: number;
    finalAmount: number;
    feesText: string;
    formatAmount: (amount: number) => string;
    /**
     * Provider primaire affiché au donateur. Influe sur le libellé
     * « Couvrir les frais X » : on n'écrit pas Stripe quand l'org
     * encaisse via PayPal (incohérent côté donateur). Karim 2026-06-28.
     */
    providerLabel?: "stripe" | "paypal";
  }>(),
  {
    providerLabel: "stripe",
  },
);

const emit = defineEmits<{
  "update:includeFees": [value: boolean];
}>();

const toggleFees = () => {
  emit("update:includeFees", !props.includeFees);
};

const providerDisplayName = computed(() =>
  props.providerLabel === "paypal" ? "PayPal" : "Stripe",
);

const { t } = useI18n();
</script>

<template>
  <div class="transition-all duration-200">
    <!-- Ligne checkbox + label - tout le div est cliquable -->
    <div
      class="flex items-center gap-2 cursor-pointer"
      role="button"
      tabindex="0"
      data-testid="donate-fees-toggle"
      :aria-pressed="includeFees"
      @click="toggleFees"
      @keydown.enter="toggleFees"
      @keydown.space.prevent="toggleFees"
    >
      <!-- UCheckbox en mode lecture seule, le toggle est géré par le div parent -->
      <UCheckbox
        :model-value="includeFees"
        color="info"
        :tabindex="-1"
        class="pointer-events-none"
      />
      <span class="text-sm text-gray-700">
        {{ t('don.optionFrais.couvrirFrais', { provider: providerDisplayName }) }}
      </span>
      <!-- ClientOnly : UTooltip (Reka) téléporte → hydration mismatch qui
           cassait l'hydratation de la page de don (init Stripe). Tooltip = 0
           valeur SSR → client-only + fallback icône statique. -->
      <ClientOnly>
        <UTooltip
          :delay-duration="0"
          :content="{
            side: 'top',
            align: 'end',
            alignOffset: -100,
            sideOffset: 12,
          }"
          :ui="{
            content:
              '!h-auto bg-white text-gray-700 shadow-lg border border-gray-200 rounded-lg p-3 text-xs w-52 whitespace-normal ring-0',
          }"
          :open-on-click="true"
        >
          <div @click.stop @touchstart.stop>
            <UIcon
              name="i-lucide-info"
              class="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors"
            />
          </div>
          <template #content>
            {{ t('don.optionFrais.infobulle') }}
          </template>
        </UTooltip>
        <template #fallback>
          <UIcon
            name="i-lucide-info"
            class="w-4 h-4 text-gray-400 cursor-help"
          />
        </template>
      </ClientOnly>
    </div>

    <!-- Description (visible uniquement si non coché) avec animation smooth via grid -->
    <div
      class="grid transition-all duration-300 ease-in-out"
      :class="
        includeFees
          ? 'grid-rows-[0fr] opacity-0'
          : 'grid-rows-[1fr] opacity-100'
      "
    >
      <div class="overflow-hidden">
        <p class="text-xs text-gray-500 pt-0 ml-6">
          {{ t('don.optionFrais.description', { provider: providerDisplayName }) }}
        </p>
      </div>
    </div>
  </div>
</template>
